import { useCallback, useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import COLORS from '@/resources/style/Themes';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import {
  getGroupEmergencyContacts,
  removeGroupEmergencyContactAuthIds,
  removeGroupEmergencyEmail,
  addGroupEmergencyContactAuthIds,
  addGroupEmergencyEmail,
  updateGroupEmergencyEmailLanguage,
  toggleAddNewMembersToEmergencyContacts,
} from '@/repository/OrganizationDAO';
import EmergencyEmailManagement from './EmergencyEmailPane';
import EmergencyRespondersPanel from './EmergencyRespondersPane';
import { GroupEmergencyContactsOnAzure } from '../../../../generated-client/models';
import LoadingData from '@/components/loading/LoadingData';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useEmergencyContactRows } from './useEmergencyContactRows';

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      // Flex column so the active panel fills the remaining page height and the
      // responders table can scroll internally instead of overflowing the page.
      style={
        value === index
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : undefined
      }
      id={`emergency-tabpanel-${index}`}
      aria-labelledby={`emergency-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            py: 3,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `emergency-tab-${index}`,
    'aria-controls': `emergency-tabpanel-${index}`,
  };
}

interface GroupEmergencyResponseTeamProps {
  getContactsForGroup: (
    groupId: string,
  ) => GroupEmergencyContactsOnAzure | undefined;
  updateGroupContacts: (updatedContacts: GroupEmergencyContactsOnAzure) => void;
}

const GroupEmergencyResponseTeam = ({
  getContactsForGroup,
  updateGroupContacts,
}: GroupEmergencyResponseTeamProps) => {
  const { groupId } = useParams<{ groupId: string }>();
  const { organization, groups } = useOrganizationUpdaterProvider();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const [apiLoading, setApiLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [autoAddNewMembers, setAutoAddNewMembers] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Get emergency contacts from parent state
  const emergencyContacts = groupId ? getContactsForGroup(groupId) : undefined;

  // Get group name from the groups context
  const groupName = useMemo(() => {
    if (!organization?.groups || !groupId) {
      return '';
    }

    // Find the group entry from organization
    const groupEntry = Object.entries(organization.groups).find(
      ([_, groupValue]) => groupValue.group_id === groupId,
    );
    return groupEntry ? groupEntry[0] : '';
  }, [organization, groupId]);

  // Initialize auto-add setting from organization data
  useEffect(() => {
    setAutoAddNewMembers(
      emergencyContacts?.add_new_members_to_emergency_contacts || false,
    );
  }, [emergencyContacts]);

  // Use custom hook to get contact rows - searches all org users, not just subscribed ones
  const { authIdContactRows } = useEmergencyContactRows({
    groups,
    contactAuthIds: emergencyContacts?.contact_auth_ids,
  });

  // Get existing contact auth_ids for filtering in the add modal
  const existingContactAuthIds = useMemo(() => {
    if (!emergencyContacts?.contact_auth_ids) return [];
    return Object.values(emergencyContacts.contact_auth_ids);
  }, [emergencyContacts]);

  // Handle adding user contacts
  const handleAddUserContacts = useCallback(
    async (authIds: string[]) => {
      if (!organization || !groupId || authIds.length === 0) return;

      setApiLoading(true);
      try {
        await addGroupEmergencyContactAuthIds({
          org_code: organization.org_code,
          group_id: groupId,
          auth_ids: authIds,
        });

        // Refresh contacts and update parent state
        const updatedContacts = await getGroupEmergencyContacts(groupId);
        updateGroupContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, groupId, updateGroupContacts],
  );

  // Handle delete for user contacts (auth_ids)
  const handleDeleteUserContacts = useCallback(
    async (authIds: string[]) => {
      if (!organization || !groupId || authIds.length === 0) return;

      setApiLoading(true);
      try {
        await removeGroupEmergencyContactAuthIds({
          org_code: organization.org_code,
          group_id: groupId,
          auth_ids: authIds,
        });

        // Refresh contacts and update parent state
        const updatedContacts = await getGroupEmergencyContacts(groupId);
        updateGroupContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, groupId, updateGroupContacts],
  );

  // Handle delete for email contact
  const handleDeleteEmailContact = useCallback(
    async (email: string) => {
      if (!organization || !groupId) return;

      setApiLoading(true);
      try {
        await removeGroupEmergencyEmail({
          org_code: organization.org_code,
          group_id: groupId,
          email,
        });

        // Refresh contacts and update parent state
        const updatedContacts = await getGroupEmergencyContacts(groupId);
        updateGroupContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, groupId, updateGroupContacts],
  );

  // Handle update email language
  const handleUpdateEmailLanguage = useCallback(
    async (email: string, languageCode: string) => {
      if (!organization || !groupId) return;

      setApiLoading(true);
      try {
        await updateGroupEmergencyEmailLanguage({
          org_code: organization.org_code,
          group_id: groupId,
          email,
          language_code: languageCode,
        });

        // Refresh contacts and update parent state
        const updatedContacts = await getGroupEmergencyContacts(groupId);
        updateGroupContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, groupId, updateGroupContacts],
  );

  // Handle add email contact
  const handleAddEmailContact = useCallback(
    async (email: string, name: string, languageCode: string) => {
      if (!organization || !groupId) return;

      setApiLoading(true);
      try {
        await addGroupEmergencyEmail({
          org_code: organization.org_code,
          group_id: groupId,
          email,
          name,
          language_code: languageCode,
        });

        // Refresh contacts and update parent state
        const updatedContacts = await getGroupEmergencyContacts(groupId);
        updateGroupContacts(updatedContacts);
      } finally {
        setApiLoading(false);
      }
    },
    [organization, groupId, updateGroupContacts],
  );

  // Handle toggle auto-add new members
  const handleToggleAutoAddNewMembers = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!groupId) return;

      const newValue = event.target.checked;
      setApiLoading(true);
      try {
        const contacts = await toggleAddNewMembersToEmergencyContacts(
          groupId,
          newValue,
        );
        updateGroupContacts(contacts);
      } finally {
        setApiLoading(false);
      }
    },
    [groupId, updateGroupContacts],
  );

  if (!organization) {
    return <LoadingSpinner />;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Sidebar width itself is now excluded by the parent page's wrapper Box
        // (see EmergencyResponderManagement.tsx); this is just the gutter after it.
        ...(isMobile ? { px: 2, pt: 0 } : { pl: '40px', pr: '40px' }),
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Typography
            sx={{
              top: -12,
              ml: '2px',
              fontSize: 16,
              fontWeight: 500,
              color: COLORS.TEXT_HEADER_GRAY,
              lineHeight: 1,
            }}
          >
            {t(StringKeys.EMERGENCY_RESPONDERS)}
          </Typography>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: COLORS.SAFEGUARD_BLUE,
              fontWeight: 400,
            }}
          >
            {groupName}
          </Typography>
        </Box>
      </Box>

      {!emergencyContacts ? (
        <Box sx={{ mt: 4 }}>
          <LoadingSpinner />
        </Box>
      ) : (
        <Box
          sx={{
            mt: 0,
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Auto-add new members checkbox */}
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoAddNewMembers}
                  onChange={handleToggleAutoAddNewMembers}
                  disabled={apiLoading}
                  sx={{
                    color: COLORS.DARK_BLUE,
                    '&.Mui-checked': {
                      color: COLORS.SAFEGUARD_BLUE,
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2">
                    {t(StringKeys.ADD_NEW_GROUP_MEMBERS_AS_RESPONDERS)}
                  </Typography>
                  <Tooltip
                    title={t(
                      StringKeys.ADD_NEW_GROUP_MEMBERS_AS_RESPONDERS_TOOLTIP,
                    )}
                  >
                    <IconButton size="small" sx={{ p: 0 }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
          </Box>

          {/* Create reusable tabs component */}
          {(() => {
            const tabsComponent = (
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="emergency contacts tabs"
              >
                <Tab
                  label={`${t(StringKeys.EMERGENCY_RESPONDERS)} (${authIdContactRows.length})`}
                  {...a11yProps(0)}
                />
                <Tab
                  label={`${t(StringKeys.EMAILS)} (${emergencyContacts?.contact_email_addresses ? Object.keys(emergencyContacts.contact_email_addresses).length : 0})`}
                  {...a11yProps(1)}
                />
              </Tabs>
            );

            return (
              <>
                {/* Responders Tab Panel */}
                <CustomTabPanel value={tabValue} index={0}>
                  <EmergencyRespondersPanel
                    contacts={authIdContactRows}
                    onAddContacts={handleAddUserContacts}
                    onDeleteContacts={handleDeleteUserContacts}
                    loading={apiLoading}
                    groups={groups || []}
                    currentGroupId={groupId || ''}
                    existingContactAuthIds={existingContactAuthIds}
                    tabControl={tabsComponent}
                    emptyMessage={t(
                      StringKeys.NO_EMERGENCY_RESPONDERS_CONFIGURED,
                    )}
                  />
                </CustomTabPanel>

                {/* Email Contacts Tab Panel */}
                <CustomTabPanel value={tabValue} index={1}>
                  <EmergencyEmailManagement
                    emailContacts={emergencyContacts?.contact_email_addresses}
                    onDelete={handleDeleteEmailContact}
                    onUpdateLanguage={handleUpdateEmailLanguage}
                    onAddEmail={handleAddEmailContact}
                    loading={apiLoading}
                    emptyMessage={t(StringKeys.NO_EMAIL_CONTACTS_CONFIGURED)}
                    tabControl={tabsComponent}
                  />
                </CustomTabPanel>
              </>
            );
          })()}
        </Box>
      )}
      <LoadingData show={apiLoading} />
    </Box>
  );
};

export default GroupEmergencyResponseTeam;
